import type { Equipment, ExerciseDto, SetKind, SetSegmentDto, WeekdayKey, WorkoutDayDto } from "../types";
import { EXERCISE_NAMES_LIST } from "../exerciseNames";
import {
  SET_KINDS,
  SET_KIND_LABEL,
  WEEKDAYS,
  WEEKDAY_LABEL,
  emptyExercise,
  newSegment,
} from "../types";

interface Props {
  day: WorkoutDayDto;
  usedWeekdays: WeekdayKey[];
  onChange: (day: WorkoutDayDto) => void;
  onRemove: () => void;
}

/**
 * One training day as a table.
 *
 * Weight and rest are here alongside name/sets/reps because the app uses both: rest drives the
 * timer between sets, and weight is what progressive overload raises. Both carry defaults, so a
 * coach who does not care can ignore them.
 */
export function DayTable({ day, usedWeekdays, onChange, onRemove }: Props) {
  function setExercise(index: number, patch: Partial<ExerciseDto>) {
    const exercises = day.exercises.map((exercise, i) =>
      i === index ? { ...exercise, ...patch } : exercise,
    );
    onChange({ ...day, exercises });
  }

  function addRow() {
    onChange({ ...day, exercises: [...day.exercises, emptyExercise()] });
  }

  function removeRow(index: number) {
    onChange({ ...day, exercises: day.exercises.filter((_, i) => i !== index) });
  }

  /**
   * The blocks hung off one exercise.
   *
   * Plans written before blocks existed have no `extras` at all, and the database drops an empty
   * list rather than storing one, so this is never read straight off the record.
   */
  function extrasOf(exercise: ExerciseDto): SetSegmentDto[] {
    return exercise.extras ?? [];
  }

  function setSegment(
    index: number,
    exercise: ExerciseDto,
    blockIndex: number,
    patch: Partial<SetSegmentDto>,
  ) {
    const extras = extrasOf(exercise).map((segment, i) =>
      i === blockIndex ? { ...segment, ...patch } : segment,
    );
    setExercise(index, { extras });
  }

  function addSegment(index: number, exercise: ExerciseDto, kind: SetKind) {
    setExercise(index, { extras: [...extrasOf(exercise), newSegment(kind, exercise)] });
  }

  function removeSegment(index: number, exercise: ExerciseDto, blockIndex: number) {
    setExercise(index, {
      extras: extrasOf(exercise).filter((_, i) => i !== blockIndex),
    });
  }

  const available = WEEKDAYS.filter(
    (weekday) => weekday === day.weekday || !usedWeekdays.includes(weekday),
  );

  return (
    <section className="card">
      <div className="day-head">
        <select
          value={day.weekday}
          onChange={(event) => onChange({ ...day, weekday: event.target.value as WeekdayKey })}
        >
          {available.map((weekday) => (
            <option key={weekday} value={weekday}>
              {WEEKDAY_LABEL[weekday]}
            </option>
          ))}
        </select>
        <input
          className="day-name"
          placeholder="Session name, e.g. Push"
          value={day.name}
          onChange={(event) => onChange({ ...day, name: event.target.value })}
        />
        <button className="ghost danger" onClick={onRemove} title="Remove day">
          Remove day
        </button>
      </div>

      <table>
        <thead>
          <tr>
            <th className="wide">Exercise</th>
            <th>Sets</th>
            <th>Reps</th>
            <th>Kg</th>
            <th>Rest (s)</th>
            <th aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {day.exercises.flatMap((exercise, index) => [
            <tr key={index}>
              <td data-label="Exercise">
                <input
                  value={exercise.name}
                  placeholder="Exercise name"
                  list={EXERCISE_NAMES_LIST}
                  onChange={(event) => setExercise(index, { name: event.target.value })}
                />
              </td>
              <td data-label="Sets">
                <input
                  type="number"
                  min={1}
                  value={exercise.sets}
                  onChange={(event) =>
                    setExercise(index, { sets: Number(event.target.value) || 0 })
                  }
                />
              </td>
              <td data-label="Reps">
                <input
                  type="number"
                  min={1}
                  value={exercise.reps}
                  onChange={(event) =>
                    setExercise(index, { reps: Number(event.target.value) || 0 })
                  }
                />
              </td>
              <td data-label="Kg">
                <input
                  type="number"
                  min={0}
                  step="0.5"
                  value={exercise.weightKg}
                  onChange={(event) =>
                    setExercise(index, { weightKg: Number(event.target.value) || 0 })
                  }
                />
              </td>
              <td data-label="Rest (s)">
                <input
                  type="number"
                  min={0}
                  step={15}
                  value={exercise.restSeconds}
                  onChange={(event) =>
                    setExercise(index, { restSeconds: Number(event.target.value) || 0 })
                  }
                />
              </td>
              <td>
                <button
                  className="ghost danger"
                  onClick={() => removeRow(index)}
                  title="Remove exercise"
                >
                  ×
                </button>
              </td>
            </tr>,
            // A block is part of the exercise above it, so it lives under that row rather than
            // becoming a line of its own in the table: the athlete does all of it as one set.
            <tr key={`${index}-blocks`} className="blocks-row">
              <td colSpan={6}>
                <div className="blocks">
                  <label className="block-equipment">
                    Equipment
                    <select
                      value={exercise.equipment ?? "barbell"}
                      onChange={(event) =>
                        setExercise(index, { equipment: event.target.value as Equipment })
                      }
                    >
                      <option value="barbell">Barbell / machine</option>
                      <option value="dumbbell">Dumbbells</option>
                    </select>
                  </label>

                  <label className="block-notes">
                    How to do it — shown on the athlete's phone behind an "i"
                    <textarea
                      rows={2}
                      placeholder="Tempo, cues, range of motion, anything they should read before the first set"
                      value={exercise.notes ?? ""}
                      onChange={(event) => setExercise(index, { notes: event.target.value })}
                    />
                  </label>

                  {extrasOf(exercise).map((segment, blockIndex) => (
                    <div className="block" key={blockIndex}>
                      <span className="block-kind">{SET_KIND_LABEL[segment.kind]}</span>
                      {segment.kind === "superset" && (
                        <input
                          value={segment.exerciseName}
                          placeholder="Exercise name"
                          list={EXERCISE_NAMES_LIST}
                          onChange={(event) =>
                            setSegment(index, exercise, blockIndex, { exerciseName: event.target.value })
                          }
                        />
                      )}
                      <label>
                        Reps
                        <input
                          type="number"
                          min={1}
                          value={segment.reps}
                          onChange={(event) =>
                            setSegment(index, exercise, blockIndex, {
                              reps: Number(event.target.value) || 0,
                            })
                          }
                        />
                      </label>
                      {segment.kind === "restPause" ? (
                        <label>
                          Pause (s)
                          <input
                            type="number"
                            min={0}
                            step={5}
                            value={segment.restSeconds}
                            onChange={(event) =>
                              setSegment(index, exercise, blockIndex, {
                                restSeconds: Number(event.target.value) || 0,
                              })
                            }
                          />
                        </label>
                      ) : (
                        <label>
                          Kg
                          <input
                            type="number"
                            min={0}
                            step="0.5"
                            value={segment.weightKg}
                            onChange={(event) =>
                              setSegment(index, exercise, blockIndex, {
                                weightKg: Number(event.target.value) || 0,
                              })
                            }
                          />
                        </label>
                      )}
                      <button
                        className="ghost danger"
                        onClick={() => removeSegment(index, exercise, blockIndex)}
                        title="Remove block"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  <div className="block-buttons">
                    {SET_KINDS.map((kind) => (
                      <button
                        key={kind}
                        className="ghost"
                        onClick={() => addSegment(index, exercise, kind)}
                      >
                        + {SET_KIND_LABEL[kind]}
                      </button>
                    ))}
                  </div>
                </div>
              </td>
            </tr>,
          ])}
        </tbody>
      </table>

      <button className="ghost" onClick={addRow}>
        + Add exercise
      </button>
    </section>
  );
}
